import clsx from 'clsx'
import debounce from 'lodash.debounce'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import useClickOutsideBox from '../hooks/useClickOutsideBox'
import { ExploreMapMarkerProps } from '../utils'

import IconButton from 'components/Luxkit/Button/IconButton'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import SolidTimesIcon from 'components/Luxkit/Icons/solid/SolidTimesIcon'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { min } from 'lib/array/arrayUtils'
import { preventDefault } from 'lib/web/eventHandlerUtils'
import ExploreSummaryCard from 'tripPlanner/components/TripMaps/SummaryCards/Explore/ExploreSummaryCard'
import { ExploreItem } from 'tripPlanner/types/common'

const OuterContainer = styled.div`
  position: absolute;
  z-index: 5;
  bottom: ${rem(16)};
  width: 100%;
  transform: translateY(0);
  transition: transform 0.2s;

  &:not(.show) {
    transform: translateY(calc(100% + ${rem(18)}));
  }
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
  z-index: 9999;
  box-shadow: ${(props) => props.theme.shadow.flat.large};
  transform: translateY(0);

  &.show {
    transform: translateY(calc(-100% - ${rem(32)}));
  }
`

const cardWidth = `min(${rem(300)}, 80vw)`

const InnerContainer = styled.div`
  width: ${cardWidth};
  margin-inline: auto;
`

const FullHeightExploreSummaryCard = styled(ExploreSummaryCard)`
  height: 100%;
`

interface Props {
  exploreItems: Array<ExploreItem>
  selectedMarker: ExploreMapMarkerProps | undefined
  onSelect: (item: ExploreItem) => void
  onDeselect: () => void
  containerRef?: React.RefObject<HTMLDivElement>
}

function ExploreCardCarousel({
  exploreItems,
  selectedMarker,
  onSelect,
  onDeselect,
  containerRef: externalContainerRef,
}: Props) {
  const internalContainerRef = useRef<HTMLDivElement | null>(null)
  const containerRef = externalContainerRef ?? internalContainerRef
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const cardElements = useRef<{ [id: string]: HTMLElement }>({})

  useClickOutsideBox(containerRef, onDeselect, {
    enabled: !!selectedMarker,
    excludeModalClicks: true,
  })

  // Scroll to correct card when pin selection changes
  useEffect(() => {
    if (
      selectedMarker &&
      carouselRef.current &&
      cardElements.current[selectedMarker.item.id]
    ) {
      // Determine distance to scroll to get the relevant card into view
      const cardRect =
        cardElements.current[selectedMarker.item.id].getBoundingClientRect()
      const currentCentre = cardRect.left + cardRect.width / 2
      const targetCentre = window.innerWidth / 2
      const scrollBy = currentCentre - targetCentre

      // Determine whether the carousel is already visible
      const carouselRect = carouselRef.current.getBoundingClientRect()
      const midPoint = (window.innerHeight + carouselRect.height) / 2
      const isAlreadyUp = carouselRect.top < midPoint

      // Rounding errors caused by screen scaling can cause scrollBy to be something weird
      // like 0.2. This causes the carousel to advance to the next card, which is not what we
      // want. Checking if scrollBy is at least 1 avoids this.
      if (Math.abs(scrollBy) >= 1) {
        carouselRef.current.scrollBy({
          left: scrollBy,
          behavior: isAlreadyUp ? 'smooth' : 'instant',
        })
      }
    }
  })

  const selectedItemRef = useAutoUpdatingRef(selectedMarker?.item)

  // Update selected item after swiping to a different card
  // Debounce to avoid updating item before smooth scroll has finished
  const onCardSelect = useMemo<() => void>(
    () =>
      debounce(() => {
        const visibleCardItem = min(exploreItems, (item) => {
          const rect = cardElements.current[item.id].getBoundingClientRect()
          const cardCentre = rect.left + rect.width / 2
          const viewportCentre = window.innerWidth / 2
          return Math.abs(cardCentre - viewportCentre)
        })
        if (visibleCardItem && visibleCardItem !== selectedItemRef.current) {
          onSelect(visibleCardItem)
        }
      }, 30),
    [exploreItems, onSelect, selectedItemRef],
  )

  // Don't update item mid-swipe
  const isSwiping = useRef(false)
  useEffect(() => {
    const carouselElement = carouselRef.current
    const startSwipe = () => {
      isSwiping.current = true
    }
    const endSwipe = () => {
      isSwiping.current = false
      onCardSelect()
    }
    if (carouselElement) {
      carouselElement.addEventListener('touchstart', startSwipe)
      carouselElement.addEventListener('touchend', endSwipe)
      return () => {
        carouselElement.removeEventListener('touchstart', startSwipe)
        carouselElement.removeEventListener('touchend', endSwipe)
      }
    }
  }, [onCardSelect])

  const onScroll = useCallback(() => {
    if (!isSwiping.current) {
      onCardSelect()
    }
  }, [onCardSelect])

  return (
    <OuterContainer
      ref={containerRef}
      onScroll={preventDefault}
      className={clsx({ show: !!selectedMarker })}
    >
      <CloseButton
        kind="primary"
        variant="ghost"
        size="medium"
        shape="circle"
        aria-label="Close"
        onClick={onDeselect}
        className={clsx({ show: !!selectedMarker })}
      >
        <SolidTimesIcon />
      </CloseButton>
      <InnerContainer>
        <CardCarousel
          ref={carouselRef}
          snap="center"
          width={cardWidth}
          gutterSize={`calc(100vw - min(${cardWidth}, 100vw))`}
          gap={12}
          onScroll={onScroll}
        >
          {exploreItems.map((item) => (
            <div
              key={item.id}
              ref={(element) => {
                if (element) cardElements.current[item.id] = element
              }}
            >
              <FullHeightExploreSummaryCard item={item} alwaysShadow />
            </div>
          ))}
        </CardCarousel>
      </InnerContainer>
    </OuterContainer>
  )
}

export default ExploreCardCarousel
